/**
 * Value Types
 *
 * TypeScript types for the Values system.
 */

export interface Value {
  id: string;
  individualId: string;
  name: string;
  description?: string;
  weight: number;
  percentage?: number; // Calculated: weight / totalWeight * 100
  congruenceScore: number;
  category?: ValueCategory;
  priority?: number;
  evidenceOfLiving?: string;
  evidenceOfViolating?: string;
  lastLivedAt?: string;
  linkedTaskId?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

export type ValueCategory =
  | 'self_transcendence'
  | 'conservation'
  | 'self_enhancement'
  | 'openness_to_change';

export const VALUE_CATEGORIES: Record<ValueCategory, string> = {
  self_transcendence: 'Self-Transcendence',
  conservation: 'Conservation',
  self_enhancement: 'Self-Enhancement',
  openness_to_change: 'Openness to Change',
};

export interface ValueListResponse {
  values: Value[];
  total: number;
  totalWeight: number;
}

export interface ValueStatsResponse {
  total: number;
  avgCongruence: number;
  strong: number; // score >= 0.7
  partial: number; // score 0.4-0.69
  gap: number; // score < 0.4
  totalWeight: number;
}

export interface CreateValueInput {
  name: string;
  description?: string;
  weight?: number;
  congruenceScore?: number;
  category?: ValueCategory;
  priority?: number;
  evidenceOfLiving?: string;
  evidenceOfViolating?: string;
}

export interface UpdateValueInput extends Partial<CreateValueInput> {
  isActive?: boolean;
}

export interface ValueEvidence {
  id: string;
  valueId: string;
  type: 'lived' | 'violated';
  description: string;
  source: string;
  recordedAt: string;
}

export interface RecordEvidenceInput {
  type: 'lived' | 'violated';
  description?: string;
  source?: string;
}

export interface GuidanceContent {
  title: string;
  content: string;
}

export interface GuidanceResponse {
  guidance: Record<string, GuidanceContent> | GuidanceContent;
}

export interface AutoTasksResponse {
  tasksCreated: Array<{
    valueId: string;
    valueName: string;
    taskId: string;
  }>;
  neglectedCount: number;
}

/**
 * Get congruence level from score
 */
export function getCongruenceLevel(score: number): 'strong' | 'partial' | 'gap' {
  if (score >= 0.7) return 'strong';
  if (score >= 0.4) return 'partial';
  return 'gap';
}

/**
 * Get color classes for congruence level
 */
export function getCongruenceColor(score: number): {
  bg: string;
  text: string;
  border: string;
  fill: string;
} {
  const level = getCongruenceLevel(score);
  switch (level) {
    case 'strong':
      return {
        bg: 'bg-green-100',
        text: 'text-green-700',
        border: 'border-green-500',
        fill: '#22c55e',
      };
    case 'partial':
      return {
        bg: 'bg-yellow-100',
        text: 'text-yellow-700',
        border: 'border-yellow-500',
        fill: '#eab308',
      };
    case 'gap':
      return {
        bg: 'bg-red-100',
        text: 'text-red-700',
        border: 'border-red-500',
        fill: '#ef4444',
      };
  }
}

